import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Star, MessageCircle, ThumbsUp } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, setRating, readonly = false }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-6 h-6 cursor-pointer transition-colors ${
            (hover || rating) >= star
              ? 'text-yellow-400 fill-current'
              : 'text-gray-300'
          }`}
          onClick={() => !readonly && setRating(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
        />
      ))}
    </div>
  );
};

const ReviewForm = ({ booking, onReviewSubmitted, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      alert("Veuillez donner une note");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/reviews`, {
        booking_id: booking.id,
        rating,
        comment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onReviewSubmitted();
    } catch (error) {
      console.error("Error submitting review:", error);
      alert("Erreur lors de l'envoi de l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center">
          <MessageCircle className="w-5 h-5 mr-2" />
          Évaluer votre expérience
        </CardTitle>
        <CardDescription>
          Comment s'est passé l'atelier "{booking.workshop_title}" ?
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Note générale</label>
            <StarRating rating={rating} setRating={setRating} />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Votre commentaire</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Partagez votre expérience avec les autres participants..."
              rows={4}
              required
            />
          </div>

          <div className="flex space-x-3">
            <Button 
              type="submit" 
              disabled={submitting || rating === 0}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? "Envoi..." : "Publier l'avis"}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
          </div>

          <div className="text-xs text-gray-500">
            💡 Note importante : Votre avis déclenchera le paiement du chef si vous donnez 3 étoiles ou plus.
            Les avis de 1-2 étoiles nécessitent une vérification administrative.
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const ReviewsList = ({ workshopId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReviews();
  }, [workshopId]);

  const loadReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/workshop/${workshopId}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mx-auto"></div>
        <p className="text-sm text-gray-500 mt-2">Chargement des avis...</p>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Aucun avis pour le moment</p>
        <p className="text-sm text-gray-400">Soyez le premier à partager votre expérience !</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Avis des participants ({reviews.length})</h3>
      
      {reviews.map((review) => (
        <Card key={review.id}>
          <CardContent className="pt-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="font-medium">{review.user_name}</span>
                  <StarRating rating={review.rating} readonly={true} />
                </div>
                <p className="text-sm text-gray-500">
                  {new Date(review.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <Badge 
                variant={review.rating >= 4 ? 'default' : review.rating >= 3 ? 'secondary' : 'destructive'}
                className={
                  review.rating >= 4 ? 'bg-green-600' : 
                  review.rating >= 3 ? 'bg-yellow-600' : 'bg-red-600'
                }
              >
                {review.rating}/5
              </Badge>
            </div>
            
            <p className="text-gray-700 mb-3">{review.comment}</p>
            
            <div className="flex items-center text-sm text-gray-500">
              <ThumbsUp className="w-4 h-4 mr-1" />
              {review.helpful_votes} personnes ont trouvé cet avis utile
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const ClientBookings = ({ user }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(null);

  useEffect(() => {
    loadUserBookings();
  }, []);

  const loadUserBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter bookings for current user
      const userBookings = response.data.filter(booking => booking.user_email === user.email);
      setBookings(userBookings);
    } catch (error) {
      console.error("Error loading bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmitted = () => {
    setShowReviewForm(null);
    loadUserBookings();
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
        <p>Chargement de vos réservations...</p>
      </div>
    );
  }

  const completedBookings = bookings.filter(booking => booking.status === 'completed');
  const reviewableBookings = completedBookings.filter(booking => !booking.review_id);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Mes Réservations</h2>
      
      {showReviewForm && (
        <ReviewForm
          booking={showReviewForm}
          onReviewSubmitted={handleReviewSubmitted}
          onCancel={() => setShowReviewForm(null)}
        />
      )}

      {reviewableBookings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-orange-600">Ateliers à évaluer</CardTitle>
            <CardDescription>
              Donnez votre avis sur les ateliers terminés
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reviewableBookings.map((booking) => (
                <div key={booking.id} className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{booking.workshop_title || "Atelier culinaire"}</h4>
                    <p className="text-sm text-gray-600">
                      {booking.participants_count} participant{booking.participants_count > 1 ? 's' : ''} • {booking.total_price}€
                    </p>
                  </div>
                  <Button 
                    onClick={() => setShowReviewForm(booking)}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Évaluer
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Historique des réservations</CardTitle>
        </CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Aucune réservation pour le moment</p>
          ) : (
            <div className="space-y-3">
              {bookings.map((booking) => (
                <div key={booking.id} className="flex justify-between items-center p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{booking.workshop_title || "Atelier culinaire"}</h4>
                    <p className="text-sm text-gray-600">
                      {booking.participants_count} participant{booking.participants_count > 1 ? 's' : ''} • {booking.total_price}€
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge 
                      variant={
                        booking.status === 'completed' ? 'default' :
                        booking.status === 'confirmed' ? 'secondary' :
                        booking.status === 'paid' ? 'default' : 'outline'
                      }
                    >
                      {booking.status === 'completed' ? 'Terminé' :
                       booking.status === 'confirmed' ? 'Confirmé' :
                       booking.status === 'paid' ? 'Payé' : booking.status}
                    </Badge>
                    {booking.review_id && (
                      <p className="text-xs text-green-600 mt-1">✓ Avis donné</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export { ReviewForm, ReviewsList, ClientBookings, StarRating };